import { useDarkMode } from '@/hooks/useDarkMode';
import { getArchetypeAccent, getArchetypeTextAccent } from '@/config/archetypes';

/**
 * ArchetypeWorldHeader
 *
 * The hero-esque banner that appears above the content of a non-Curated
 * archetype world. Renders the archetype's title in its signature font, its
 * tagline, and an accent-tinted backdrop.
 *
 * For the Curated archetype we render nothing — Curated is our default and
 * keeps the existing Browse layout untouched.
 */
export default function ArchetypeWorldHeader({ archetype }) {
  const darkMode = useDarkMode();
  if (!archetype || archetype.slug === 'curated') return null;

  const isDark = darkMode.isDark;
  const accent = getArchetypeAccent(archetype, isDark);
  const textAccent = getArchetypeTextAccent(archetype, isDark);
  const hero = archetype.heroStyle;

  // Hero style variants — each defined by background treatment only. Layout
  // and typography stay consistent so the "3 visual axes" rule holds.
  // Backdrops derive from the archetype's accent so the colour language
  // stays consistent even if the accent changes (all Mementa-on-brand).
  const heroStyles = {
    cinematic: {
      // Big radial + strong gradient — expansive/travel feel
      background: isDark
        ? `radial-gradient(circle at 20% 30%, ${accent}66 0%, transparent 60%), linear-gradient(135deg, #1A1F2E 0%, #2D4354 100%)`
        : `radial-gradient(circle at 20% 30%, ${accent}55 0%, transparent 60%), linear-gradient(135deg, ${accent}30 0%, #FFFAF5 100%)`,
    },
    craft: {
      // Warm sideways gradient — studio-natural feel
      background: isDark
        ? `linear-gradient(90deg, ${accent}55 0%, rgba(45,67,84,0.9) 100%)`
        : `linear-gradient(90deg, ${accent}44 0%, #FFFAF5 100%)`,
    },
    workshop: {
      // Diagonal gradient — angled workbench feel
      background: isDark
        ? `linear-gradient(45deg, ${accent}55 0%, rgba(45,67,84,0.9) 100%)`
        : `linear-gradient(45deg, ${accent}44 0%, #FFFAF5 100%)`,
    },
    museum: {
      // Radial edge glow — framed/heirloom feel
      background: isDark
        ? `radial-gradient(ellipse at center, rgba(45,67,84,0.95) 0%, ${accent}66 100%)`
        : `radial-gradient(ellipse at center, #FFFAF5 0%, ${accent}44 100%)`,
    },
  };
  const style = heroStyles[hero] || heroStyles.cinematic;

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-6 sm:mb-8 -mx-4 sm:mx-0"
      data-testid={`archetype-world-header-${archetype.slug}`}
      style={{
        background: style.background,
        padding: '32px 24px',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
      }}
    >
      {/* Subtle badge / eyebrow */}
      <div className="max-w-3xl">
        <span
          className="inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full mb-4"
          style={{
            color: textAccent,
            background: `${accent}33`,
            border: `1px solid ${accent}77`,
          }}
        >
          {archetype.subtitle}
        </span>

        <h1
          className="mb-3"
          style={{
            fontFamily: archetype.fontFamily,
            ...archetype.titleStyle,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            lineHeight: 1.05,
            color: isDark ? '#FFFAF5' : '#20212B',
          }}
        >
          {archetype.title}
        </h1>

        <p
          className="text-sm sm:text-base max-w-xl"
          style={{
            color: isDark ? 'rgba(255,250,245,0.8)' : 'rgba(83,65,69,0.9)',
            fontFamily: 'Playfair Display, serif',
          }}
        >
          {archetype.tagline}
        </p>
      </div>
    </div>
  );
}
